import React from 'react';
import { GameButton } from '@/components/ui';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';

interface TestHubScreenProps {
  onGoHome: () => void;
}

const testRoutes = [
  {
    path: '/test/visual-styles',
    name: 'Visual Styles',
    description: 'Test visual style themes (pixel retro, modern, hand-drawn, clay, neon)',
    icon: 'palette' as const,
    variant: 'primary' as const,
  },
  {
    path: '/test/design-system',
    name: 'Design System',
    description: 'Comprehensive component library, themes, colors, typography, and spacing',
    icon: 'grid' as const,
    variant: 'info' as const,
  },
  {
    path: '/test/entity-compare',
    name: 'Entity Compare',
    description: 'Visual comparison of character vs all enemy types at different states and sizes',
    icon: 'grid' as const,
    variant: 'warning' as const,
  },
  {
    path: '/test/3d-showcase',
    name: '3D Asset Showcase',
    description: 'All characters, tiles, enemies, and collectibles rendered in Three.js 3D with props and effects',
    icon: 'grid' as const,
    variant: 'primary' as const,
  },
  {
    path: '/game?level=0',
    name: 'Tutorial Level',
    description: 'Play the tutorial level to test intro/exit animations and controls',
    icon: 'play' as const,
    variant: 'success' as const,
  },
  {
    path: '/game?level=1',
    name: 'Level 1',
    description: 'Play level 1 to test standard gameplay',
    icon: 'levels' as const,
    variant: 'success' as const,
  },
  {
    path: '/shop',
    name: 'Shop',
    description: 'Test shop UI, currency display, and purchase flow',
    icon: 'shop' as const,
    variant: 'warning' as const,
  },
  {
    path: '/worlds',
    name: 'Worlds',
    description: 'Test the world map navigation and level selection',
    icon: 'worlds' as const,
    variant: 'secondary' as const,
  },
  {
    path: '/upgrades',
    name: 'Upgrades',
    description: 'Test meta-upgrade system and progression',
    icon: 'upgrades' as const,
    variant: 'secondary' as const,
  },
  {
    path: '/characters',
    name: 'Characters',
    description: 'Test character selection and customization',
    icon: 'user' as const,
    variant: 'primary' as const,
  },
  {
    path: '/settings',
    name: 'Settings',
    description: 'Test settings screen, theme switching, and sound controls',
    icon: 'settings' as const,
    variant: 'info' as const,
  },
  {
    path: '/profile',
    name: 'Profile',
    description: 'Test user profile, stats display, and achievement tracking',
    icon: 'trophy' as const,
    variant: 'warning' as const,
  },
  {
    path: '/levels',
    name: 'Level Select',
    description: 'Test level selection grid, lock states, and star ratings',
    icon: 'levelSelect' as const,
    variant: 'primary' as const,
  },
];

const TestHubScreen: React.FC<TestHubScreenProps> = ({ onGoHome }) => {
  const navigate = useNavigate();

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--pixel-bg-primary)',
        padding: '20px',
        paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))',
        paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
        overflow: 'auto',
      }}
    >
      {/* Header */}
      <motion.div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '24px',
        }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <GameButton onClick={onGoHome} variant="secondary" icon="home" text="Home" />
        <div>
          <h1
            style={{
              color: 'var(--pixel-text-primary)',
              fontSize: '24px',
              fontFamily: "'Press Start 2P', monospace",
              margin: 0,
            }}
          >
            Test Hub
          </h1>
          <p
            style={{
              color: 'var(--pixel-text-secondary)',
              fontSize: '12px',
              margin: '4px 0 0',
              fontFamily: "'Press Start 2P', monospace",
            }}
          >
            All test routes and pages
          </p>
        </div>
      </motion.div>

      {/* Route Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '16px',
          maxWidth: '1200px',
          margin: '0 auto',
        }}
      >
        {testRoutes.map((route, index) => (
          <motion.div
            key={route.path}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            style={{
              background: 'var(--pixel-bg-secondary)',
              borderRadius: '12px',
              padding: '20px',
              cursor: 'pointer',
              border: '2px solid var(--pixel-border)',
              position: 'relative',
              overflow: 'hidden',
            }}
            whileHover={{ scale: 1.02, borderColor: 'var(--pixel-primary)' }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(route.path)}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  background: 'rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <GameButton
                  onClick={() => navigate(route.path)}
                  variant={route.variant}
                  icon={route.icon}
                  text=""
                  size="small"
                />
              </div>
              <div style={{ flex: 1 }}>
                <h3
                  style={{
                    color: 'var(--pixel-text-primary)',
                    fontSize: '14px',
                    fontFamily: "'Press Start 2P', monospace",
                    margin: '0 0 8px',
                  }}
                >
                  {route.name}
                </h3>
                <p
                  style={{
                    color: 'var(--pixel-text-secondary)',
                    fontSize: '11px',
                    lineHeight: '1.5',
                    margin: 0,
                  }}
                >
                  {route.description}
                </p>
                <code
                  style={{
                    color: 'var(--pixel-info)',
                    fontSize: '10px',
                    marginTop: '8px',
                    display: 'block',
                    opacity: 0.7,
                  }}
                >
                  {route.path}
                </code>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default TestHubScreen;
